import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Flashlight, FlashlightOff, Focus, ScanLine } from "lucide-react";

export default function BarcodeScannerDialog({ open, onOpenChange, onDetected }) {
  const divId = "scanner-root";
  const scannerRef = useRef(null);
  const runningRef = useRef(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [scanning, setScanning] = useState(false);

  // Toggle torch/flashlight
  const toggleTorch = useCallback(async () => {
    try {
      const scanner = scannerRef.current;
      if (!scanner) return;
      
      // Access the video track from the scanner
      const videoElement = document.querySelector(`#${divId} video`);
      if (!videoElement || !videoElement.srcObject) return;
      
      const track = videoElement.srcObject.getVideoTracks()[0];
      if (!track) return;
      
      const capabilities = track.getCapabilities?.();
      if (!capabilities?.torch) {
        toast.error("Torch not supported on this device");
        return;
      }
      
      const newTorchState = !torchOn;
      await track.applyConstraints({
        advanced: [{ torch: newTorchState }]
      });
      setTorchOn(newTorchState);
    } catch (e) {
      console.error("Torch toggle error:", e);
      toast.error("Could not toggle torch");
    }
  }, [torchOn]);

  useEffect(() => {
    if (!open) {
      setTorchOn(false);
      setTorchSupported(false);
      setScanning(false);
      return;
    }
    let cancelled = false;

    const start = async () => {
      try {
        const mod = await import("html5-qrcode");
        if (cancelled) return;
        const { Html5Qrcode } = mod;
        const scanner = new Html5Qrcode(divId);
        scannerRef.current = scanner;
        
        await scanner.start(
          { facingMode: "environment" },
          { 
            fps: 10, 
            qrbox: { width: 260, height: 140 },
            aspectRatio: 1.777778, // 16:9
          },
          (decodedText) => {
            if (!runningRef.current) return;
            runningRef.current = false;
            setScanning(false);
            scanner.stop().catch(() => {}).finally(() => {
              try { scanner.clear(); } catch (_e) {}
              onDetected?.(decodedText);
              onOpenChange(false);
            });
          },
          () => {}
        );
        runningRef.current = true;
        setScanning(true);
        
        // Check torch support after camera starts
        setTimeout(() => {
          const videoElement = document.querySelector(`#${divId} video`);
          if (videoElement?.srcObject) {
            const track = videoElement.srcObject.getVideoTracks()[0];
            const capabilities = track?.getCapabilities?.();
            setTorchSupported(!!capabilities?.torch);
          }
        }, 500);
        
      } catch (e) {
        // Browser may refuse camera on http or no devices available
        toast.error(
          "Unable to start camera. Check browser permissions or try manual entry."
        );
        onOpenChange(false);
      }
    };

    const t = setTimeout(start, 120);

    return () => {
      cancelled = true;
      clearTimeout(t);
      const scanner = scannerRef.current;
      if (scanner && runningRef.current) {
        runningRef.current = false;
        // Best-effort cleanup; swallow html5-qrcode errors
        scanner
          .stop()
          .catch(() => {})
          .finally(() => {
            try { scanner.clear(); } catch (_e) {}
          });
      }
      scannerRef.current = null;
      setTorchOn(false);
      setScanning(false);
    };
  }, [open, onDetected, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="scanner-dialog">
        <DialogHeader className="p-4 pb-2">
          <DialogTitle className="font-heading tracking-tight flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#9E6D5B]" />
            Scan Barcode
          </DialogTitle>
          <DialogDescription>
            Position the barcode within the frame. Hold steady for best results.
          </DialogDescription>
        </DialogHeader>
        
        {/* Scanner container with overlay */}
        <div className="relative bg-black">
          <div
            id={divId}
            className="w-full aspect-[4/3]"
            data-testid="scanner-video"
          />
          
          {/* Focus guide overlay */}
          {scanning && (
            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
              {/* Semi-transparent overlay with cutout effect */}
              <div className="absolute inset-0 bg-black/40" />
              
              {/* Scanning frame */}
              <div className="relative z-10 w-[280px] h-[160px] border-2 border-white/80 rounded-lg">
                {/* Corner markers */}
                <div className="absolute -top-1 -left-1 w-6 h-6 border-t-4 border-l-4 border-[#9E6D5B] rounded-tl-lg" />
                <div className="absolute -top-1 -right-1 w-6 h-6 border-t-4 border-r-4 border-[#9E6D5B] rounded-tr-lg" />
                <div className="absolute -bottom-1 -left-1 w-6 h-6 border-b-4 border-l-4 border-[#9E6D5B] rounded-bl-lg" />
                <div className="absolute -bottom-1 -right-1 w-6 h-6 border-b-4 border-r-4 border-[#9E6D5B] rounded-br-lg" />
                
                {/* Scanning line animation */}
                <div className="absolute inset-x-2 top-1/2 h-0.5 bg-gradient-to-r from-transparent via-[#9E6D5B] to-transparent animate-pulse" />
              </div>
              
              {/* Hint text */}
              <div className="absolute bottom-4 left-0 right-0 text-center">
                <span className="bg-black/60 text-white text-xs px-3 py-1.5 rounded-full">
                  <Focus className="w-3 h-3 inline-block mr-1" />
                  Align barcode within frame
                </span>
              </div>
            </div>
          )}
        </div>
        
        {/* Controls */}
        <div className="p-4 pt-3 flex items-center justify-between gap-2 bg-[hsl(var(--muted))]">
          <div className="flex items-center gap-2">
            {/* Torch button - only show if supported */}
            {torchSupported && (
              <Button
                variant={torchOn ? "default" : "outline"}
                size="sm"
                onClick={toggleTorch}
                className={torchOn ? "bg-amber-500 hover:bg-amber-600" : ""}
                data-testid="scanner-torch-btn"
              >
                {torchOn ? (
                  <>
                    <Flashlight className="w-4 h-4 mr-1" />
                    Torch On
                  </>
                ) : (
                  <>
                    <FlashlightOff className="w-4 h-4 mr-1" />
                    Torch
                  </>
                )}
              </Button>
            )}
            
            {/* Status indicator */}
            {scanning && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Scanning...
              </span>
            )}
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            data-testid="scanner-close-btn"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
